import { Alert, Snackbar, Stack, TextField } from "@mui/material";
import * as React from "react";

import { CopyLinkButton } from "@/shared/components/CopyLinkButton";
import { FormattedDateTime } from "@/shared/datetime";
import { useUpdateAdminFeedback } from "@/shared/domain/admin/hooks/useAdminFeedback";
import { type AgentFeedbackRecord } from "@/shared/domain/agentThread";
import { Button } from "@/shared/foundation/primitives";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import styles from "./FeedbackTriagePanel.module.css";
import {
  buildSavePayload,
  isDirty as isFormDirty,
} from "./feedbackTriageState";
import { buildAdminFeedbackLink } from "./shareLinks";

interface FeedbackTriagePanelProps {
  feedback: AgentFeedbackRecord;
}

interface Toast {
  severity: "success" | "error";
  message: string;
}

/**
 * Admin-only editor for a feedback row's triage fields
 * (``admin_label``, ``admin_note``, ``resolved``).
 *
 * Renders as an inline form on top of the selected feedback row. Changes are
 * sent via ``PUT /v1/admin/feedback/<id>`` and the listing invalidated on
 * success.
 */
export const FeedbackTriagePanel: React.FC<FeedbackTriagePanelProps> = ({
  feedback,
}) => {
  const updater = useUpdateAdminFeedback();
  const [label, setLabel] = React.useState<string>(feedback.admin_label ?? "");
  const [note, setNote] = React.useState<string>(feedback.admin_note ?? "");
  const [toast, setToast] = React.useState<Toast | null>(null);

  const showMutationError = (
    error: Error,
    action: "save" | "toggleResolved",
  ) => {
    ErrorMonitoringService.captureError(error, {
      context: { feedbackId: feedback.feedback_id, action },
    });
    setToast({
      severity: "error",
      message: `Couldn't update feedback: ${error.message}`,
    });
  };

  // Reset local form state when a *different* feedback record is shown.
  // Keyed only on ``feedback_id``: a refetch that returns the same row with
  // updated triage columns must not blow away whatever the admin has typed
  // since opening the panel.
  React.useEffect(() => {
    setLabel(feedback.admin_label ?? "");
    setNote(feedback.admin_note ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional; see comment above
  }, [feedback.feedback_id]);

  const dirty = isFormDirty({
    persistedLabel: feedback.admin_label ?? null,
    persistedNote: feedback.admin_note ?? null,
    draftLabel: label,
    draftNote: note,
  });

  const save = () => {
    updater.mutate(
      {
        feedbackId: feedback.feedback_id,
        request: buildSavePayload({ draftLabel: label, draftNote: note }),
      },
      {
        onSuccess: () =>
          setToast({ severity: "success", message: "Feedback updated" }),
        onError: (error) => showMutationError(error, "save"),
      },
    );
  };

  const toggleResolved = () => {
    const nextResolved = !feedback.resolved;
    updater.mutate(
      {
        feedbackId: feedback.feedback_id,
        request: { resolved: nextResolved },
      },
      {
        onSuccess: () =>
          setToast({
            severity: "success",
            message: nextResolved ? "Marked resolved" : "Re-opened",
          }),
        onError: (error) => showMutationError(error, "toggleResolved"),
      },
    );
  };

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.title}>Triage</span>
        <CopyLinkButton
          url={buildAdminFeedbackLink({
            sessionId: feedback.thread_id,
            feedbackId: feedback.feedback_id,
          })}
          tooltip="Copy a link to this feedback for another admin"
        />
        {feedback.resolved && feedback.resolved_by && feedback.resolved_at && (
          <span className={styles.resolvedMeta}>
            Resolved by {feedback.resolved_by} on{" "}
            <FormattedDateTime value={feedback.resolved_at} />
          </span>
        )}
      </div>
      <Stack spacing={1} className={styles.form}>
        <TextField
          label="Admin label"
          size="small"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. follow-up, dup, data-quality"
          fullWidth
        />
        <TextField
          label="Admin note"
          size="small"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          rows={3}
          placeholder="Internal-only notes"
          fullWidth
        />
        <Stack direction="row" spacing={1} className={styles.actions}>
          <Button
            variant="contained"
            size="small"
            onClick={save}
            disabled={!dirty || updater.isPending}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={toggleResolved}
            disabled={updater.isPending}
          >
            {feedback.resolved ? "Re-open" : "Mark resolved"}
          </Button>
        </Stack>
      </Stack>
      {/* Mount the Snackbar only when there is a toast to show, so its
          child is always a real Alert and not ``undefined`` (MUI tolerates
          ``undefined`` but logs a warning, and the conditional makes the
          intent obvious). */}
      {toast !== null && (
        <Snackbar
          open
          autoHideDuration={toast.severity === "error" ? 6000 : 2500}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity={toast.severity}
            variant="filled"
            onClose={() => setToast(null)}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      )}
    </div>
  );
};
